import { useId, useState, type FormEvent } from "react";
import { useCreateDomain } from "../../api/queries";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface BindDomainModalProps {
  open: boolean;
  projectId: string;
  serviceId: string;
  serviceName: string;
  onClose: () => void;
}

/** A loose client-side shape hint only — the server is the actual validator
 * (RFC-1123: at least two dot-separated labels, 1..63 chars each, letters/
 * digits/hyphens, no leading/trailing hyphen). This never blocks submission
 * on its own; it just gives an operator a head start before the round trip,
 * and the server's own 400 message renders verbatim if it still refuses. */
const HOSTNAME_HINT_RE = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Binds a DNS hostname to a service (ADR-0026 §3/§5, ADR-0027). Binding pushes
 * a routing manifest to the service's nodes (#290) so Kelpie's edge proxy
 * begins serving the hostname once DNS points at the node and its ACME
 * certificate issues. The copy promises only what's true: the hostname routes,
 * but the operator still has to point DNS at the node themselves.
 */
export function BindDomainModal({ open, projectId, serviceId, serviceName, onClose }: BindDomainModalProps) {
  const hostnameFieldId = useId();
  const portFieldId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateDomain(projectId, serviceId);

  const [hostname, setHostname] = useState("");
  const [targetPort, setTargetPort] = useState("");

  function handleClose() {
    setHostname("");
    setTargetPort("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedHostname = hostname.trim();
    if (!trimmedHostname || !portValid) return;
    mutation.mutate(
      { hostname: trimmedHostname, targetPort: Number.parseInt(targetPort, 10) },
      {
        onSuccess: (created) => {
          pushToast({ title: `Bound "${created.hostname}" to "${serviceName}".`, tone: "success" });
          handleClose();
        },
      },
    );
  }

  const hostnameLooksValid = hostname.trim() === "" || HOSTNAME_HINT_RE.test(hostname.trim());
  const parsedPort = targetPort.trim() ? Number.parseInt(targetPort, 10) : undefined;
  const portValid = Number.isInteger(parsedPort) && (parsedPort as number) >= 1 && (parsedPort as number) <= 65535;
  const canSubmit = hostname.trim().length > 0 && portValid && !mutation.isPending;

  return (
    <Modal open={open} title="Bind domain" onClose={handleClose} initialFocusId={hostnameFieldId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't bind this domain."}
          </p>
        )}

        <label className="form-field" htmlFor={hostnameFieldId}>
          <span>Hostname</span>
          <input
            id={hostnameFieldId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="app.example.com"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            required
          />
          {!hostnameLooksValid && (
            <span className="form-field__error">
              Use a real domain, e.g. app.example.com — no scheme, path, or port here.
            </span>
          )}
        </label>

        <label className="form-field" htmlFor={portFieldId}>
          <span>Target port</span>
          <input
            id={portFieldId}
            type="number"
            min={1}
            max={65535}
            value={targetPort}
            onChange={(e) => setTargetPort(e.target.value)}
            required
          />
          <span className="form-field__hint">The port this service listens on inside its container.</span>
        </label>

        <p className="form-field__hint">
          Binding starts routing this hostname to the service. Point an A or CNAME record at this node's address; it
          serves once DNS resolves and its certificate issues.
        </p>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Binding…" : "Bind domain"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
